/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { classes, Container, Slot } from '@adobe-commerce/elsie/lib';
import {
  CardLoader,
  EmptyList,
  OrdersListAction,
  OrdersListCard,
} from '@/account/components';
import { Header, Pagination, Picker } from '@adobe-commerce/elsie/components';
import { useOrdersList } from '@/account/hooks/containers/useOrdersList';
import { OrdersListWrapperProps } from '@/account/types';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/account/components/OrdersListWrapper/OrdersListWrapper.css';
import { useOrderSelectList } from '@/account/hooks/containers/useOrderSelectList';

export const OrdersListWrapper: Container<OrdersListWrapperProps> = ({
  className,
  withHeader = true,
  minifiedView = false,
  withThumbnails = true,
  withFilter = true,
  ordersInMinifiedView = 1,
  pageSize = 10,
  routeOrdersList,
  routeOrderDetails,
  routeReturnDetails,
  routeTracking,
  routeOrderProduct,
  slots,
}) => {
  const minifiedViewKey = minifiedView ? 'minifiedView' : 'fullSizeView';

  const translations = useText({
    containerTitle: `Account.${minifiedViewKey}.OrdersList.containerTitle`,
    dateOrderPlaced: `Account.${minifiedViewKey}.OrdersList.dateOrderPlaced`,
  });

  const {
    selectableDateList,
    selectedDate,
    handleSelectDate,
    selectedPage,
    setSelectedPage,
    handleSetFirstOrderDate,
  } = useOrderSelectList();

  const { pageInfo, loading, orderHistoryListItems, placeholderImage } =
    useOrdersList({
      minifiedView,
      pageSize,
      ordersInMinifiedView,
      selectedDate,
      selectedPage,
      handleSetFirstOrderDate,
    });

  return (
    <div>
      {withHeader ? (
        <Header
          aria-label={translations.containerTitle}
          role="region"
          title={translations.containerTitle}
          divider={!minifiedView}
          className={minifiedView ? 'account-orders-list-header' : ''}
        />
      ) : null}

      <div className={classes(['account-orders-list', className])}>
        {!minifiedView && withFilter ? (
          <div className={'account-orders-list__date-select'}>
            <span>{translations.dateOrderPlaced}</span>
            <Picker
              value={selectedDate}
              name="orderDatePicker"
              options={selectableDateList}
              handleSelect={handleSelectDate}
            />
          </div>
        ) : null}

        {!loading ? (
          <>
            {orderHistoryListItems.length ? (
              <>
                {orderHistoryListItems.map((el, index) => {
                  return (
                    <OrdersListCard
                      placeholderImage={placeholderImage}
                      routeTracking={routeTracking}
                      routeOrderProduct={routeOrderProduct}
                      routeReturnDetails={routeReturnDetails}
                      minifiedView={minifiedView}
                      item={el}
                      withThumbnails={withThumbnails}
                      key={index}
                      slots={slots}
                    >
                      {slots?.OrdersListAction ? (
                        <Slot
                          data-testid={`ordersListActionSlot_${index}`}
                          name="OrdersListAction"
                          slot={slots?.OrdersListAction}
                          context={{ orderHistoryListItem: el }}
                        />
                      ) : (
                        <OrdersListAction
                          minifiedView={minifiedView}
                          orderNumber={el.number}
                          orderToken={el.token}
                          routeOrderDetails={routeOrderDetails}
                        />
                      )}
                    </OrdersListCard>
                  );
                })}
              </>
            ) : (
              <EmptyList
                isEmpty={!orderHistoryListItems.length}
                typeList="orders"
                minifiedView={minifiedView}
              />
            )}
          </>
        ) : (
          <>
            {Array.from(Array(pageInfo?.pageSize).keys()).map((i) => (
              <CardLoader
                testId={'orderSkeletonLoader'}
                withCard={false}
                key={i}
              />
            ))}
          </>
        )}
        {!minifiedView && pageInfo?.totalPages > 1 ? (
          <Pagination
            totalPages={pageInfo?.totalPages}
            currentPage={selectedPage}
            onChange={setSelectedPage}
          />
        ) : null}

        {minifiedView ? (
          <OrdersListAction
            minifiedView={minifiedView}
            routeOrdersList={routeOrdersList}
          />
        ) : null}
      </div>
    </div>
  );
};
